<svelte:options runes={true} />

<script lang="ts">
  import { CommandId } from '@commands';
  import type { Point } from '@geom';
  import { localeManager } from '@reactive';
  import { StringId } from '@strings';
  import {
    APP_ICON_MAGNIFIER,
    APP_ICON_X_AXIS,
    APP_ICON_Y_AXIS,
    isActivationEvent,
    SlFontSize
  } from '@ui';
  import { fixedNumber } from '@utils';
  import Icon from './Icon.svelte';
  import StatusBar from './StatusBar.svelte';
  import type { CommandManager } from '@commands';

  let {
    zoom = $bindable(),
    minZoom,
    maxZoom,
    pan = $bindable(),
    commandManager
  }: {
    zoom: number;
    minZoom: number;
    maxZoom: number;
    pan: Point;
    commandManager: CommandManager;
  } = $props();

  let showDevTools = $state(true);

  function rangeValue(event: CustomEvent): number {
    return parseFloat((event.target as HTMLInputElement).value);
  }

  function zoomChange(event: CustomEvent) {
    zoom = rangeValue(event);
  }

  function xPanChange(event: CustomEvent) {
    pan = pan.withX(rangeValue(event));
  }

  function yPanChange(event: CustomEvent) {
    pan = pan.withY(rangeValue(event));
  }

  function percentFormatter(value: number) {
    return fixedNumber(Math.round(value * 100), 0) + '%';
  }

  function roundToIntFormatter(value: number) {
    return Math.round(value).toString();
  }

  function zoomToFit() {
    commandManager.dispatch(CommandId.ZOOM_TO_FIT);
  }

  function zoom100() {
    commandManager.dispatch(CommandId.ZOOM_100_PERCENT);
  }
</script>

<style>
  .range {
    --track-color-active: var(--fl-range-track);
    --track-color-inactive: var(--fl-range-track);
  }
  .h-stack {
    display: flex;
    flex-direction: row;
    gap: 20px;
  }

  .v-stack {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
</style>

{#snippet zoomRange()}
  <div style="width: 200px; gap: 5px;" class="h-stack">
    <Icon icon={APP_ICON_MAGNIFIER} size={SlFontSize.LG} />
    <sl-range
      style="flex-grow: 1;"
      class="range"
      max={maxZoom}
      min={minZoom}
      onsl-input={zoomChange}
      step={0.01}
      tooltipFormatter={percentFormatter}
      value={zoom}
    ></sl-range>
  </div>
{/snippet}

{#snippet zoomToFitButton()}
  <sl-button
    onclick={zoomToFit}
    onkeydown={(event: KeyboardEvent) => {
      if (isActivationEvent(event)) {
        zoomToFit();
      }
    }}
    role="button"
    size="small"
    tabindex="0"
  >
    {localeManager.localizedString(StringId.ZOOM_TO_FIT_COMMAND)}
  </sl-button>
{/snippet}

{#snippet zoom100Button()}
  <sl-button
    onclick={zoom100}
    onkeydown={(event: KeyboardEvent) => {
      if (isActivationEvent(event)) {
        zoom100();
      }
    }}
    role="button"
    size="small"
    tabindex="0"
  >
    100%
  </sl-button>
{/snippet}

{#snippet panXRange()}
  <div style="width: 200px; gap: 5px;" class="h-stack">
    <Icon icon={APP_ICON_X_AXIS} size={SlFontSize.LG} />
    <sl-range
      style="width: 200px;"
      class="range"
      max={1000}
      min={-1000}
      onsl-input={xPanChange}
      step={1}
      tooltipFormatter={roundToIntFormatter}
      value={pan.x}
    ></sl-range>
  </div>
{/snippet}

{#snippet panYRange()}
  <div style="width: 200px; gap: 5px;" class="h-stack">
    <Icon icon={APP_ICON_Y_AXIS} size={SlFontSize.LG} />
    <sl-range
      style="width: 200px;"
      class="range"
      max={1000}
      min={-1000}
      onsl-input={yPanChange}
      step={1}
      tooltipFormatter={roundToIntFormatter}
      value={pan.y}
    ></sl-range>
  </div>
{/snippet}

<div id="BottomBar" class="v-stack">
  <StatusBar></StatusBar>
  {#if showDevTools}
    <div style="padding: 10px;" class="h-stack">
      <div class="v-stack">
        {@render zoomRange()}
        <div class="h-stack">
          {@render zoomToFitButton()}
          {@render zoom100Button()}
        </div>
      </div>
      <div class="v-stack">
        {@render panXRange()}
        {@render panYRange()}
      </div>
    </div>
  {/if}
</div>
